import React, { useState, useEffect } from 'react';
import { TrendingUp, Bell, X, Activity, MapPin, ExternalLink, DollarSign } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ScatterChart, Scatter, ReferenceLine, Cell } from 'recharts';
import API_URL from '../config/ApiUrl';
import { useTheme } from '../arrangement/ThemeContext';

const QuadrantTooltip = ({ colors, active, payload, label }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div style={{
        background: colors.bgTertiary,
        border: '1px solid #00d4aa',
        borderRadius: '12px',
        padding: '12px 16px',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
        maxWidth: '200px'
      }}>
        <p style={{ 
          margin: '0 0 8px 0', 
          color: '#e5e5e5', 
          fontSize: '14px',
          fontWeight: '600'
        }}>
          {data.full_name}
        </p>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          marginBottom: '4px'
        }}>
          <span style={{ color: '#00d4aa', fontSize: '16px', fontWeight: '700' }}>
            ${data.price}
          </span>
          <span style={{ color: '#8b5cf6', fontSize: '14px' }}>
            {data.distance} mi
          </span>
        </div>
        {data.variant && (
          <p style={{ margin: 0, color: '#888', fontSize: '12px' }}>
            {data.variant}
          </p>
        )}
      </div>
    );
  }
  return null;
};

const CustomTooltip = ({ colors, active, payload, label }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div style={{
        background: colors.bgTertiary,
        border: '1px solid #00d4aa',
        borderRadius: '12px',
        padding: '12px 16px',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)',
        maxWidth: '200px'
      }}>
        <p style={{ 
          margin: '0 0 8px 0', 
          color: '#e5e5e5', 
          fontSize: '14px',
          fontWeight: '600'
        }}>
          {data.dispensary_name}
        </p>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          marginBottom: '4px'
        }}>
          <div style={{
            width: '8px',
            height: '8px',
            borderRadius: '50%',
            background: '#00d4aa'
          }} />
          <span style={{ 
            color: '#00d4aa', 
            fontSize: '16px',
            fontWeight: '700'
          }}>
            ${payload[0].value}
          </span>
        </div>
        {data.variant && (
          <p style={{ 
            margin: 0, 
            color: '#888', 
            fontSize: '12px' 
          }}>
            {data.variant}
          </p>
        )}
        {data.distance && (
          <p style={{ 
            margin: 0, 
            color: '#888', 
            fontSize: '12px' 
          }}>
            {data.distance} miles away
          </p>
        )}
      </div>
    );
  }
  return null;
};

const PriceTracker = ({ product }) => {
  const [showChart, setShowChart] = useState(false);
  const [priceData, setPriceData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [priceSummary, setPriceSummary] = useState(null);
  const [locationOption, setLocationOption] = useState('all');
  const [customLocation, setCustomLocation] = useState('');
  const [showLocationSelector, setShowLocationSelector] = useState(false);
  const { colors } = useTheme(); 
  // Load price summary on component mount using YOUR real API
  useEffect(() => {
    loadPriceSummary();
  }, [product.id]);

  const loadPriceSummary = async () => {
    try {
      // Use your actual pricing data from the product object first
      if (product.pricing && product.pricing.has_pricing) {
        const realPriceSummary = {
          has_pricing: product.pricing.has_pricing,
          average_price: product.pricing.average_price,
          price_range: {
            min: product.pricing.min_price,
            max: product.pricing.max_price
          },
          location_count: product.pricing.location_count
        };
        setPriceSummary(realPriceSummary);
        return;
      }

      // Fallback to API call if needed
      const response = await fetch(`${API_URL}/api/products/${product.id}/price-summary`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        setPriceSummary(data);
      }
    } catch (err) {
      console.error('Error loading price summary:', err);
    }
  };

  const loadDetailedPricing = async () => {
    setLoading(true);
    setError(null);
    
    try {
      let locationParams = '';
      
      if (locationOption === 'current') {
        // Use current location
        if (navigator.geolocation) {
          try {
            const position = await new Promise((resolve, reject) => {
              navigator.geolocation.getCurrentPosition(resolve, reject, {
                timeout: 10000,
                enableHighAccuracy: false
              });
            });
            
            const lat = position.coords.latitude;
            const lng = position.coords.longitude;
            locationParams = `?lat=${lat}&lng=${lng}&radius=50`;
          } catch (geoError) {
            console.log('Geolocation failed, falling back to all dispensaries');
            setLocationOption('all');
          }
        }
      } else if (locationOption === 'choose' && customLocation.trim()) {
        // Geocode the custom location
        try {
          const geocodeResponse = await fetch(
            `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(customLocation)}.json?access_token=pk.eyJ1IjoiaGlnaG1pbmQiLCJhIjoiY2w2aTNtb2ZhMDc3YjNkcGVzOTY4NDBndCJ9.rVqoJUP2cUGpPG5d7U9Xfg&limit=1`
          );
          
          if (geocodeResponse.ok) {
            const geocodeData = await geocodeResponse.json();
            if (geocodeData.features && geocodeData.features.length > 0) {
              const [lng, lat] = geocodeData.features[0].center;
              locationParams = `?lat=${lat}&lng=${lng}&radius=50`;
            }
          }
        } catch (geocodeError) {
          console.log('Geocoding failed, falling back to all dispensaries');
        }
      }
      
      // Call YOUR actual API endpoint
      const response = await fetch(`${API_URL}/api/products/${product.id}/dispensaries${locationParams}`, {
        credentials: 'include'
      });
      
      if (response.ok) {
        const data = await response.json();
        console.log('Real dispensary data:', data);
        
        const chartData = data.dispensaries
          .filter(d => d.product_info?.price && d.product_info?.in_stock)
          .map(d => ({
            dispensary_name: d.name.length > 15 
              ? d.name.substring(0, 15) + '...' 
              : d.name,
            full_name: d.name,
            price: d.product_info.price,
            variant: d.product_info.variant,
            distance: d.distance ? parseFloat(d.distance.toFixed(1)) : null,
            url: d.product_info.url,
            city: d.city,
            state: d.state
          }))
          .sort((a, b) => {
            // Sort by distance if available, otherwise by price
            if (locationParams && a.distance !== null && b.distance !== null) {
              return a.distance - b.distance;
            }
            return a.price - b.price;
          });
        
        console.log('Processed chart data:', chartData);
        setPriceData(chartData);
        setShowLocationSelector(false);
      } else {
        setError('Failed to load pricing data');
      }
    } catch (err) {
      console.error('Error loading detailed pricing:', err);
      setError('Error loading pricing data');
    } finally {
      setLoading(false);
    }
  };

  const handleShowChart = () => {
    setShowChart(true);
    setLocationOption('all');
    setShowLocationSelector(false);
    loadDetailedPricing();
  };

  // Determine chart type based on location option
  const shouldShowQuadrantChart = locationOption === 'current' || locationOption === 'choose';
  const hasLocationData = priceData && priceData.every(item => item.distance !== null);

  // Calculate quadrant boundaries
  const getQuadrantData = () => {
    if (!priceData || !hasLocationData) return null;

    const prices = priceData.map(d => d.price);
    const distances = priceData.map(d => d.distance);
    
    const avgPrice = prices.reduce((a, b) => a + b, 0) / prices.length;
    const avgDistance = distances.reduce((a, b) => a + b, 0) / distances.length;

    return {
      avgPrice,
      avgDistance,
      minPrice: Math.min(...prices),
      maxPrice: Math.max(...prices),
      minDistance: Math.min(...distances),
      maxDistance: Math.max(...distances)
    };
  };

  const quadrantData = getQuadrantData();

  // Get quadrant label for a data point
  const getQuadrantLabel = (price, distance) => {
    if (!quadrantData) return '';
    const { avgPrice, avgDistance } = quadrantData;
    
    if (price <= avgPrice && distance <= avgDistance) return 'Sweet Spot';
    if (price <= avgPrice && distance > avgDistance) return 'Good Deal';
    if (price > avgPrice && distance <= avgDistance) return 'Convenient';
    return 'Expensive & Far';
  };

  // Get quadrant color
  const getQuadrantColor = (price, distance) => {
    if (!quadrantData) return '#00d4aa';
    const { avgPrice, avgDistance } = quadrantData;
    
    if (price <= avgPrice && distance <= avgDistance) return '#10b981'; // Green - Sweet spot
    if (price <= avgPrice && distance > avgDistance) return '#3b82f6'; // Blue - Good deal
    if (price > avgPrice && distance <= avgDistance) return '#f59e0b'; // Orange - Convenient
    return '#ef4444'; // Red - Expensive & far
  };

  // Use YOUR real pricing data
  const displayPrice = priceSummary?.has_pricing 
    ? `$${priceSummary.average_price}` 
    : product.price;
    
  const priceRange = priceSummary?.has_pricing 
    ? `$${priceSummary.price_range.min} - $${priceSummary.price_range.max}`
    : null;

  return (
    <>
      {/* Price Tracker Component */}
      <div style={{
        background: 'linear-gradient(135deg, #1a2e2e 0%, #0f2027 100%)',
        border: '1px solid #2d4a4a',
        borderRadius: '16px',
        padding: '20px'
      }}>
        <h3 style={{
          margin: '0 0 16px 0',
          fontSize: '18px',
          fontWeight: '600',
          color: '#00d4aa',
          display: 'flex',
          alignItems: 'center',
          gap: '8px'
        }}>
          <DollarSign size={18} />
          Price Overview
        </h3>
        
        <div style={{ marginBottom: '16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <span style={{ fontSize: '24px', fontWeight: '700', color: '#00d4aa' }}>
              {displayPrice}
            </span>
            {priceSummary?.has_pricing && (
              <span style={{ fontSize: '14px', color: '#10b981' }}>
                avg of {priceSummary.location_count} locations
              </span>
            )}
          </div>
          
          {priceRange && (
            <div style={{ marginBottom: '8px' }}>
              <span style={{ fontSize: '12px', color: '#888' }}>
                Range: {priceRange}
              </span>
            </div>
          )}
          
          <button
            onClick={handleShowChart}
            style={{
              background: 'transparent',
              border: 'none',
              color: '#888',
              fontSize: '12px',
              cursor: 'pointer',
              padding: '0',
              textDecoration: 'underline'
            }}
          >
            {priceSummary?.has_pricing 
              ? `View prices at ${priceSummary.location_count} dispensaries →`
              : 'View pricing info →'
            }
          </button>
        </div>

        <button style={{
          width: '100%',
          padding: '12px 16px',
          background: colors.bgCard,
          color: '#e5e5e5',
          border: '1px solid #333',
          borderRadius: '8px',
          cursor: 'pointer',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          fontSize: '14px',
          fontWeight: '600',
          transition: 'all 0.3s ease'
        }}
        onClick={handleShowChart}
        onMouseEnter={(e) => e.currentTarget.style.borderColor = '#00d4aa'}
        onMouseLeave={(e) => e.currentTarget.style.borderColor = '#333'}
        >
          <MapPin size={16} />
          Compare Prices
        </button>
      </div>

      {/* Price Comparison Dialog */}
      {showChart && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.8)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000,
          padding: '20px'
        }}>
          <div style={{
            background: colors.bgTertiary,
            borderRadius: '20px',
            width: '100%',
            maxWidth: '1200px',
            maxHeight: '90vh',
            overflow: 'auto',
            border: '1px solid #333',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.8)'
          }}>
            {/* Header */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '24px 32px',
              borderBottom: '1px solid #333'
            }}>
              <div>
                <h2 style={{
                  margin: '0 0 4px 0',
                  fontSize: '24px',
                  fontWeight: '700',
                  color: '#e5e5e5',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px'
                }}>
                  <Activity size={24} style={{ color: '#00d4aa' }} />
                  Price Comparison
                </h2>
                <p style={{
                  margin: 0,
                  color: '#888',
                  fontSize: '14px'
                }}>
                  {product.name} - {product.brand}
                </p>
              </div>
              <button
                onClick={() => setShowChart(false)}
                style={{
                  background: 'transparent',
                  border: 'none',
                  color: '#888',
                  cursor: 'pointer',
                  padding: '8px',
                  borderRadius: '8px',
                  transition: 'all 0.3s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#2a2a2a';
                  e.currentTarget.style.color = '#e5e5e5';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.color = '#888';
                }}
              >
                <X size={20} />
              </button>
            </div>

            {/* Content */}
            <div style={{ padding: '32px' }}>
              {/* Location Selector */}
              <div style={{
                background: '#0a0a0a',
                border: '1px solid #333',
                borderRadius: '16px',
                padding: '24px',
                marginBottom: '24px'
              }}>
                <h3 style={{
                  margin: '0 0 20px 0',
                  color: '#e5e5e5',
                  fontSize: '16px',
                  fontWeight: '600'
                }}>
                  Choose Location Preference
                </h3>
                
                <div style={{
                  display: 'grid',
                  gap: '12px',
                  marginBottom: '20px'
                }}>
                  {/* Use Current Location */}
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '12px 16px',
                    background: locationOption === 'current' ? '#2a2a2a' : '#1a1a1a',
                    border: `1px solid ${locationOption === 'current' ? '#00d4aa' : '#333'}`,
                    borderRadius: '8px',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}>
                    <input
                      type="radio"
                      name="location"
                      value="current"
                      checked={locationOption === 'current'}
                      onChange={(e) => setLocationOption(e.target.value)}
                      style={{ marginRight: '12px' }}
                    />
                    <div>
                      <div style={{ color: '#e5e5e5', fontWeight: '600', marginBottom: '4px' }}>
                        Use My Current Location
                      </div>
                      <div style={{ color: '#888', fontSize: '12px' }}>
                        Find dispensaries near you - shows quadrant chart
                      </div>
                    </div>
                  </label>

                  {/* Choose Location */}
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '12px 16px',
                    background: locationOption === 'choose' ? '#2a2a2a' : '#1a1a1a',
                    border: `1px solid ${locationOption === 'choose' ? '#00d4aa' : '#333'}`,
                    borderRadius: '8px',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}>
                    <input
                      type="radio"
                      name="location"
                      value="choose"
                      checked={locationOption === 'choose'}
                      onChange={(e) => setLocationOption(e.target.value)}
                      style={{ marginRight: '12px' }}
                    />
                    <div style={{ flex: 1 }}>
                      <div style={{ color: '#e5e5e5', fontWeight: '600', marginBottom: '4px' }}>
                        Choose a Location
                      </div>
                      <div style={{ color: '#888', fontSize: '12px', marginBottom: '8px' }}>
                        Enter a city, state, or zip code - shows quadrant chart
                      </div>
                      {locationOption === 'choose' && (
                        <input
                          type="text"
                          placeholder="e.g., Denver, CO or 90210"
                          value={customLocation}
                          onChange={(e) => setCustomLocation(e.target.value)}
                          style={{
                            width: '100%',
                            padding: '8px 12px',
                            background: '#0a0a0a',
                            border: '1px solid #333',
                            borderRadius: '6px',
                            color: '#e5e5e5',
                            fontSize: '14px',
                            outline: 'none'
                          }}
                          onFocus={(e) => e.target.style.borderColor = '#00d4aa'}
                          onBlur={(e) => e.target.style.borderColor = '#333'}
                        />
                      )}
                    </div>
                  </label>

                  {/* All Dispensaries */}
                  <label style={{
                    display: 'flex',
                    alignItems: 'center',
                    padding: '12px 16px',
                    background: locationOption === 'all' ? '#2a2a2a' : '#1a1a1a',
                    border: `1px solid ${locationOption === 'all' ? '#00d4aa' : '#333'}`,
                    borderRadius: '8px',
                    cursor: 'pointer',
                    transition: 'all 0.3s ease'
                  }}>
                    <input
                      type="radio"
                      name="location"
                      value="all"
                      checked={locationOption === 'all'}
                      onChange={(e) => setLocationOption(e.target.value)}
                      style={{ marginRight: '12px' }}
                    />
                    <div>
                      <div style={{ color: '#e5e5e5', fontWeight: '600', marginBottom: '4px' }}>
                        Show All Dispensaries
                      </div>
                      <div style={{ color: '#888', fontSize: '12px' }}>
                        View all available locations - shows bar chart
                      </div>
                    </div>
                  </label>
                </div>

                <div style={{
                  display: 'flex',
                  gap: '12px',
                  justifyContent: 'flex-end'
                }}>
                  <button
                    onClick={loadDetailedPricing}
                    disabled={locationOption === 'choose' && !customLocation.trim()}
                    style={{
                      padding: '8px 16px',
                      background: (locationOption !== 'choose' || customLocation.trim()) 
                        ? 'linear-gradient(135deg, #00d4aa 0%, #10b981 100%)'
                        : '#333',
                      color: (locationOption !== 'choose' || customLocation.trim()) ? '#0a0a0a' : '#666',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: (locationOption !== 'choose' || customLocation.trim()) ? 'pointer' : 'not-allowed',
                      fontSize: '14px',
                      fontWeight: '600'
                    }}
                  >
                    Update Chart
                  </button>
                </div>
              </div>

              {!loading && !error && priceData && (
                <>
                  {/* Stats Cards */}
                  {priceSummary?.has_pricing && (
                    <div style={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                      gap: '16px',
                      marginBottom: '32px'
                    }}>
                      <div style={{
                        background: 'linear-gradient(135deg, #2a2a2a 0%, #1f1f1f 100%)',
                        border: '1px solid #333',
                        borderRadius: '12px',
                        padding: '16px',
                        textAlign: 'center'
                      }}>
                        <div style={{
                          color: '#00d4aa',
                          fontSize: '20px',
                          fontWeight: '700',
                          marginBottom: '4px'
                        }}>
                          ${priceSummary.average_price}
                        </div>
                        <div style={{ color: '#888', fontSize: '12px' }}>Average Price</div>
                      </div>

                      <div style={{
                        background: 'linear-gradient(135deg, #2a2a2a 0%, #1f1f1f 100%)',
                        border: '1px solid #333',
                        borderRadius: '12px',
                        padding: '16px',
                        textAlign: 'center'
                      }}>
                        <div style={{
                          color: '#10b981',
                          fontSize: '20px',
                          fontWeight: '700',
                          marginBottom: '4px'
                        }}>
                          ${priceSummary.price_range.min}
                        </div>
                        <div style={{ color: '#888', fontSize: '12px' }}>Best Deal</div>
                      </div>

                      <div style={{
                        background: 'linear-gradient(135deg, #2a2a2a 0%, #1f1f1f 100%)',
                        border: '1px solid #333',
                        borderRadius: '12px',
                        padding: '16px',
                        textAlign: 'center'
                      }}>
                        <div style={{
                          color: '#ef4444',
                          fontSize: '20px',
                          fontWeight: '700',
                          marginBottom: '4px'
                        }}>
                          ${priceSummary.price_range.max}
                        </div>
                        <div style={{ color: '#888', fontSize: '12px' }}>Highest Price</div>
                      </div>

                      <div style={{
                        background: 'linear-gradient(135deg, #2a2a2a 0%, #1f1f1f 100%)',
                        border: '1px solid #333',
                        borderRadius: '12px',
                        padding: '16px',
                        textAlign: 'center'
                      }}>
                        <div style={{
                          color: '#8b5cf6',
                          fontSize: '20px',
                          fontWeight: '700',
                          marginBottom: '4px'
                        }}>
                          {priceSummary.location_count}
                        </div>
                        <div style={{ color: '#888', fontSize: '12px' }}>Dispensaries</div>
                      </div>
                    </div>
                  )}

                  {/* Chart Container */}
                  <div style={{
                    background: '#0a0a0a',
                    border: '1px solid #333',
                    borderRadius: '16px',
                    padding: '24px',
                    marginBottom: '24px'
                  }}>
                    <h3 style={{
                      margin: '0 0 20px 0',
                      color: '#e5e5e5',
                      fontSize: '16px',
                      fontWeight: '600'
                    }}>
                      {shouldShowQuadrantChart && hasLocationData 
                        ? 'Price vs Distance Analysis'
                        : 'Price Comparison by Dispensary'
                      }
                    </h3>
                    
                    {/* Conditional Chart Rendering */}
                    {shouldShowQuadrantChart && hasLocationData && quadrantData ? (
                      <>
                        {/* Quadrant Chart */}
                        <div style={{ height: '500px' }}>
                          <ResponsiveContainer width="100%" height="100%">
                            <ScatterChart
                              data={priceData}
                              margin={{ top: 20, right: 80, left: 20, bottom: 80 }}
                            >
                              <CartesianGrid strokeDasharray="3 3" stroke="#333" opacity={0.3} />
                              
                              <XAxis 
                                type="number"
                                dataKey="distance"
                                stroke="#888"
                                fontSize={12}
                                tickLine={false}
                                axisLine={false}
                                domain={[0, quadrantData.maxDistance + 1]}
                                label={{ value: 'Distance (miles)', position: 'insideBottom', offset: -10, style: { textAnchor: 'middle', fill: '#888' } }}
                              />
                              
                              <YAxis 
                                type="number"
                                dataKey="price"
                                stroke="#888"
                                fontSize={12}
                                tickLine={false}
                                axisLine={false}
                                domain={[quadrantData.minPrice - 5, quadrantData.maxPrice + 5]}
                                tickFormatter={(value) => `$${value}`}
                                label={{ value: 'Price ($)', angle: -90, position: 'insideLeft', style: { textAnchor: 'middle', fill: '#888' } }}
                              />

                              {/* Reference Lines for Quadrants */}
                              <ReferenceLine 
                                x={quadrantData.avgDistance} 
                                stroke="#555" 
                                strokeDasharray="5 5" 
                                opacity={0.6}
                              />
                              <ReferenceLine 
                                y={quadrantData.avgPrice} 
                                stroke="#555" 
                                strokeDasharray="5 5" 
                                opacity={0.6}
                              />
                              
                              <Tooltip content={<QuadrantTooltip colors={colors} />} />
                              
                              <Scatter dataKey="price" fill="#00d4aa">
                                {priceData.map((entry, index) => (
                                  <Cell 
                                    key={`cell-${index}`} 
                                    fill={getQuadrantColor(entry.price, entry.distance)}
                                  />
                                ))}
                              </Scatter>
                            </ScatterChart>
                          </ResponsiveContainer>
                        </div>

                        {/* Quadrant Legend */}
                        <div style={{
                          display: 'grid',
                          gridTemplateColumns: '1fr 1fr',
                          gap: '16px',
                          marginTop: '24px'
                        }}>
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '12px',
                            background: 'rgba(16, 185, 129, 0.1)',
                            border: '1px solid rgba(16, 185, 129, 0.3)',
                            borderRadius: '8px'
                          }}>
                            <div style={{
                              width: '12px',
                              height: '12px',
                              borderRadius: '50%',
                              background: '#10b981'
                            }} />
                            <div>
                              <div style={{ color: '#10b981', fontWeight: '600', fontSize: '14px' }}>Sweet Spot</div>
                              <div style={{ color: '#888', fontSize: '12px' }}>Low Price, Close Distance</div>
                            </div>
                          </div>

                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '12px',
                            background: 'rgba(245, 158, 11, 0.1)',
                            border: '1px solid rgba(245, 158, 11, 0.3)',
                            borderRadius: '8px'
                          }}>
                            <div style={{
                              width: '12px',
                              height: '12px',
                              borderRadius: '50%',
                              background: '#f59e0b'
                            }} />
                            <div>
                              <div style={{ color: '#f59e0b', fontWeight: '600', fontSize: '14px' }}>Convenient</div>
                              <div style={{ color: '#888', fontSize: '12px' }}>High Price, Close Distance</div>
                            </div>
                          </div>

                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '12px',
                            background: 'rgba(59, 130, 246, 0.1)',
                            border: '1px solid rgba(59, 130, 246, 0.3)',
                            borderRadius: '8px'
                          }}>
                            <div style={{
                              width: '12px',
                              height: '12px',
                              borderRadius: '50%',
                              background: '#3b82f6'
                            }} />
                            <div>
                              <div style={{ color: '#3b82f6', fontWeight: '600', fontSize: '14px' }}>Good Deal</div>
                              <div style={{ color: '#888', fontSize: '12px' }}>Low Price, Far Distance</div>
                            </div>
                          </div>

                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '12px',
                            background: 'rgba(239, 68, 68, 0.1)',
                            border: '1px solid rgba(239, 68, 68, 0.3)',
                            borderRadius: '8px'
                          }}>
                            <div style={{
                              width: '12px',
                              height: '12px',
                              borderRadius: '50%',
                              background: '#ef4444'
                            }} />
                            <div>
                              <div style={{ color: '#ef4444', fontWeight: '600', fontSize: '14px' }}>Expensive & Far</div>
                              <div style={{ color: '#888', fontSize: '12px' }}>High Price, Far Distance</div>
                            </div>
                          </div>
                        </div>
                      </>
                    ) : (
                      /* Bar Chart for "All Dispensaries" view */
                      <div style={{ height: '400px' }}>
                        <ResponsiveContainer width="100%" height="100%">
                          <BarChart
                            data={priceData}
                            margin={{ top: 20, right: 30, left: 20, bottom: 60 }}
                          >
                            <CartesianGrid 
                              strokeDasharray="3 3" 
                              stroke="#333" 
                              opacity={0.3}
                            />
                            
                            <XAxis 
                              dataKey="dispensary_name"
                              stroke="#888"
                              fontSize={11}
                              tickLine={false}
                              axisLine={false}
                              angle={-45}
                              textAnchor="end"
                              height={80}
                            />
                            
                            <YAxis 
                              stroke="#888"
                              fontSize={12}
                              tickLine={false}
                              axisLine={false}
                              tickFormatter={(value) => `${value}`}
                            />
                            
                            <Tooltip content={<CustomTooltip colors={colors}/>} />
                            
                            <Bar
                              dataKey="price"
                              fill="#00d4aa"
                              radius={[4, 4, 0, 0]}
                              stroke="#10b981"
                              strokeWidth={1}
                            />
                          </BarChart>
                        </ResponsiveContainer>
                      </div>
                    )}
                  </div>

                  {/* Dispensary List */}
                  <div style={{
                    background: '#0a0a0a',
                    border: '1px solid #333',
                    borderRadius: '16px',
                    padding: '24px'
                  }}>
                    <h3 style={{
                      margin: '0 0 20px 0',
                      color: '#e5e5e5',
                      fontSize: '16px',
                      fontWeight: '600'
                    }}>
                      All Locations
                      {shouldShowQuadrantChart && hasLocationData && (
                        <span style={{ fontSize: '12px', color: '#888', fontWeight: '400', marginLeft: '8px' }}>
                          (sorted by {locationOption === 'all' ? 'price' : 'distance'})
                        </span>
                      )}
                    </h3>
                    
                    <div style={{ 
                      display: 'grid', 
                      gap: '12px',
                      maxHeight: '300px',
                      overflow: 'auto'
                    }}>
                      {[...priceData]
                        .sort((a, b) => {
                          // Sort by distance if location-based, otherwise by price
                          if (shouldShowQuadrantChart && hasLocationData) {
                            return a.distance - b.distance;
                          }
                          return a.price - b.price;
                        })
                        .map((item, index) => (
                        <div 
                          key={index}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            padding: '12px 16px',
                            background: colors.bgTertiary,
                            border: shouldShowQuadrantChart && hasLocationData 
                              ? `1px solid ${getQuadrantColor(item.price, item.distance)}40`
                              : '1px solid #333',
                            borderRadius: '8px',
                            transition: 'all 0.3s ease'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.borderColor = shouldShowQuadrantChart && hasLocationData
                              ? getQuadrantColor(item.price, item.distance)
                              : '#00d4aa';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.borderColor = shouldShowQuadrantChart && hasLocationData
                              ? `${getQuadrantColor(item.price, item.distance)}40`
                              : '#333';
                          }}
                        >
                          <div style={{ flex: 1 }}>
                            <div style={{
                              fontWeight: '600',
                              color: '#e5e5e5',
                              marginBottom: '4px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px'
                            }}>
                              {item.full_name}
                              {shouldShowQuadrantChart && hasLocationData && (
                                <span style={{
                                  fontSize: '10px',
                                  padding: '2px 6px',
                                  borderRadius: '4px',
                                  background: `${getQuadrantColor(item.price, item.distance)}20`,
                                  color: getQuadrantColor(item.price, item.distance),
                                  border: `1px solid ${getQuadrantColor(item.price, item.distance)}40`
                                }}>
                                  {getQuadrantLabel(item.price, item.distance)}
                                </span>
                              )}
                            </div>
                            <div style={{
                              fontSize: '12px',
                              color: '#888',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px'
                            }}>
                              {item.city && item.state && (
                                <span>{item.city}, {item.state}</span>
                              )}
                              {item.distance && (
                                <span>• {item.distance} mi</span>
                              )}
                              {item.variant && (
                                <span>• {item.variant}</span>
                              )}
                            </div>
                          </div>
                          
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px'
                          }}>
                            <span style={{
                              fontSize: '18px',
                              fontWeight: '700',
                              color: shouldShowQuadrantChart && hasLocationData
                                ? getQuadrantColor(item.price, item.distance)
                                : '#00d4aa'
                            }}>
                              ${item.price}
                            </span>
                            
                            {item.url && (
                              <a
                                href={item.url}
                                target="_blank"
                                rel="noopener noreferrer"
                                style={{
                                  padding: '6px',
                                  background: colors.bgCard,
                                  border: '1px solid #333',
                                  borderRadius: '6px',
                                  color: '#888',
                                  textDecoration: 'none',
                                  transition: 'all 0.3s ease'
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.borderColor = '#00d4aa';
                                  e.currentTarget.style.color = '#00d4aa';
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.borderColor = '#333';
                                  e.currentTarget.style.color = '#888';
                                }}
                              >
                                <ExternalLink size={14} />
                              </a>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </>
              )}

              {loading && (
                <div style={{ 
                  textAlign: 'center', 
                  padding: '40px',
                  color: '#888'
                }}>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    border: '3px solid #333',
                    borderTop: '3px solid #00d4aa',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite',
                    margin: '0 auto 16px'
                  }} />
                  Loading pricing data...
                </div>
              )}

              {error && (
                <div style={{
                  textAlign: 'center',
                  padding: '40px',
                  color: '#ef4444'
                }}>
                  <p>{error}</p>
                  <button
                    onClick={loadDetailedPricing}
                    style={{
                      marginTop: '16px',
                      padding: '8px 16px',
                      background: '#00d4aa',
                      color: '#0a0a0a',
                      border: 'none',
                      borderRadius: '6px',
                      cursor: 'pointer'
                    }}
                  >
                    Try Again
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </>
  );
};

export default PriceTracker;